import React from "react";
import { Head, Link } from "@inertiajs/react";
import {
  Container,
  Title,
  Text,
  Table,
  Button,
  Group,
  Badge,
  ActionIcon,
  Box,
  Paper,
  Stack,
  Card,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconEye,
  IconExternalLink,
} from "@tabler/icons-react";
import AppShell from "../../../Components/Layout/AppShell";

interface Project {
  id: number;
  slug: string;
  title: string;
  description: string;
  client_name?: string;
  project_url?: string;
  project_type: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface Props {
  projects: Project[];
}

const getTypeColor = (type: string) => {
  switch (type) {
    case "Technical Development":
      return "orange";
    case "Digital Media":
      return "violet";
    case "Marketing & Strategy":
      return "green";
    case "Teaching & Events":
      return "blue";
    case "Business Consulting":
      return "pink";
    default:
      return "gray";
  }
};

const ProjectCard = ({
  project,
  onDelete,
}: {
  project: Project;
  onDelete: (id: number) => void;
}) => (
  <Card
    shadow="md"
    radius="md"
    p="lg"
    style={{
      background: "rgba(26, 27, 30, 0.95)",
      border: "1px solid rgba(55, 58, 64, 0.4)",
    }}
  >
    <Stack gap="md">
      {/* Header with title and type */}
      <Group justify="space-between" align="flex-start">
        <Stack gap={4} style={{ flex: 1 }}>
          <Text fw={600} size="lg" style={{ color: "white" }} lineClamp={2}>
            {project.title}
          </Text>
          {project.client_name && (
            <Text size="sm" style={{ color: "rgba(161, 161, 170, 0.8)" }}>
              Client: {project.client_name}
            </Text>
          )}
        </Stack>
        <Badge
          color={getTypeColor(project.project_type)}
          variant="light"
          size="sm"
          style={{
            background: `rgba(${
              getTypeColor(project.project_type) === "orange"
                ? "249, 115, 22"
                : getTypeColor(project.project_type) === "violet"
                ? "168, 85, 247"
                : getTypeColor(project.project_type) === "green"
                ? "34, 197, 94"
                : getTypeColor(project.project_type) === "blue"
                ? "59, 130, 246"
                : "236, 72, 153"
            }, 0.1)`,
            border: `1px solid rgba(${
              getTypeColor(project.project_type) === "orange"
                ? "249, 115, 22"
                : getTypeColor(project.project_type) === "violet"
                ? "168, 85, 247"
                : getTypeColor(project.project_type) === "green"
                ? "34, 197, 94"
                : getTypeColor(project.project_type) === "blue"
                ? "59, 130, 246"
                : "236, 72, 153"
            }, 0.2)`,
            flexShrink: 0,
          }}
        >
          {project.project_type}
        </Badge>
      </Group>

      {/* Description */}
      <Text
        size="sm"
        style={{ color: "rgba(161, 161, 170, 0.7)" }}
        lineClamp={3}
      >
        {project.description}
      </Text>

      {/* Created date */}
      <Text size="xs" style={{ color: "rgba(161, 161, 170, 0.6)" }}>
        Created {new Date(project.created_at).toLocaleDateString()}
      </Text>

      {/* Actions */}
      <Group gap="xs" justify="flex-end" pt="xs">
        {project.project_url && (
          <ActionIcon
            size="sm"
            variant="light"
            color="blue"
            component="a"
            href={project.project_url}
            target="_blank"
            title="View Project"
            style={{
              background: "rgba(59, 130, 246, 0.1)",
              border: "1px solid rgba(59, 130, 246, 0.2)",
              color: "rgba(59, 130, 246, 0.9)",
            }}
          >
            <IconExternalLink size={14} />
          </ActionIcon>
        )}
        <ActionIcon
          size="sm"
          variant="light"
          color="orange"
          component={Link}
          href={`/admin/projects/${project.id}/edit`}
          title="Edit Project"
          style={{
            background: "rgba(249, 115, 22, 0.1)",
            border: "1px solid rgba(249, 115, 22, 0.2)",
            color: "rgba(249, 115, 22, 0.9)",
          }}
        >
          <IconEdit size={14} />
        </ActionIcon>
        <ActionIcon
          size="sm"
          variant="light"
          color="red"
          onClick={() => onDelete(project.id)}
          title="Delete Project"
          style={{
            background: "rgba(239, 68, 68, 0.1)",
            border: "1px solid rgba(239, 68, 68, 0.2)",
            color: "rgba(239, 68, 68, 0.9)",
          }}
        >
          <IconTrash size={14} />
        </ActionIcon>
      </Group>
    </Stack>
  </Card>
);

const AdminProjectsIndex: React.FC<Props> = ({ projects }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isSmallScreen = useMediaQuery("(max-width: 576px)");

  const handleDelete = (projectId: number) => {
    if (confirm("Are you sure you want to delete this project?")) {
      // Use Inertia's delete method
      window.location.href = `/admin/projects/${projectId}`;
      // In a real implementation, you'd use Inertia.delete()
    }
  };

  return (
    <AppShell>
      <Head title="Admin - Projects">
        <meta name="robots" content="noindex, nofollow" />
      </Head>

      <Box
        style={{
          background:
            "linear-gradient(180deg, #0a0a0b 0%, #111113 50%, #0a0a0b 100%)",
          minHeight: "100vh",
        }}
      >
        <Container size="xl" py="xl">
          <Stack gap="xl">
            {/* Header */}
            <Paper
              shadow="xl"
              radius="md"
              p="xl"
              style={{
                background: "rgba(26, 27, 30, 0.95)",
                border: "1px solid rgba(55, 58, 64, 0.4)",
              }}
            >
              <Group justify="space-between" align="center">
                <Stack gap="xs">
                  <Title
                    order={1}
                    style={{
                      color: "white",
                      fontFamily:
                        'Montserrat, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      fontWeight: 700,
                      background:
                        "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    Portfolio Admin
                  </Title>
                  <Text size="lg" style={{ color: "rgba(161, 161, 170, 0.9)" }}>
                    Manage your portfolio projects
                  </Text>
                </Stack>

                <Group gap="md">
                  {!isSmallScreen && (
                    <Button
                      variant="outline"
                      color="gray"
                      leftSection={<IconEye size={16} />}
                      component="a"
                      href="/"
                      target="_blank"
                      style={{
                        borderColor: "rgba(55, 58, 64, 0.4)",
                        color: "rgba(161, 161, 170, 0.9)",
                      }}
                    >
                      View Portfolio
                    </Button>
                  )}
                  <Button
                    variant="gradient"
                    gradient={{ from: "orange.6", to: "orange.7", deg: 135 }}
                    leftSection={<IconPlus size={16} />}
                    component={Link}
                    href="/admin/projects/new"
                    style={{ fontWeight: 600 }}
                  >
                    New Project
                  </Button>
                </Group>
              </Group>
            </Paper>

            {/* Projects Content - Table for desktop, Cards for mobile */}
            {projects.length > 0 ? (
              <>
                {!isMobile ? (
                  // Desktop/Tablet Table View
                  <Paper
                    shadow="xl"
                    radius="md"
                    style={{
                      background: "rgba(26, 27, 30, 0.95)",
                      border: "1px solid rgba(55, 58, 64, 0.4)",
                      overflow: "hidden",
                    }}
                  >
                    <Table highlightOnHover>
                      <Table.Thead
                        style={{ background: "rgba(20, 21, 23, 0.8)" }}
                      >
                        <Table.Tr>
                          <Table.Th
                            style={{
                              fontWeight: 600,
                              color: "rgba(161, 161, 170, 0.9)",
                              border: "none",
                              width: "30%",
                            }}
                          >
                            Project
                          </Table.Th>
                          <Table.Th
                            style={{
                              fontWeight: 600,
                              color: "rgba(161, 161, 170, 0.9)",
                              border: "none",
                            }}
                          >
                            Type
                          </Table.Th>
                          <Table.Th
                            style={{
                              fontWeight: 600,
                              color: "rgba(161, 161, 170, 0.9)",
                              border: "none",
                            }}
                          >
                            Created
                          </Table.Th>
                          <Table.Th
                            style={{
                              fontWeight: 600,
                              color: "rgba(161, 161, 170, 0.9)",
                              border: "none",
                            }}
                          >
                            Actions
                          </Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {projects.map((project) => (
                          <Table.Tr
                            key={project.id}
                            style={{
                              backgroundColor: "transparent",
                              borderBottom: "1px solid rgba(55, 58, 64, 0.3)",
                              transition: "background-color 0.2s ease",
                            }}
                            className="hover:bg-gray-800/30"
                          >
                            <Table.Td style={{ border: "none", width: "40%" }}>
                              <Stack gap={4}>
                                <Text fw={500} style={{ color: "white" }}>
                                  {project.title}
                                </Text>
                                <Text
                                  size="sm"
                                  style={{ color: "rgba(161, 161, 170, 0.7)" }}
                                  lineClamp={1}
                                >
                                  {project.description}
                                </Text>
                              </Stack>
                            </Table.Td>
                            <Table.Td style={{ border: "none" }}>
                              <Badge
                                color={getTypeColor(project.project_type)}
                                variant="light"
                                size="sm"
                                style={{
                                  background: `rgba(${
                                    getTypeColor(project.project_type) ===
                                    "orange"
                                      ? "249, 115, 22"
                                      : getTypeColor(project.project_type) ===
                                        "violet"
                                      ? "168, 85, 247"
                                      : getTypeColor(project.project_type) ===
                                        "green"
                                      ? "34, 197, 94"
                                      : getTypeColor(project.project_type) ===
                                        "blue"
                                      ? "59, 130, 246"
                                      : "236, 72, 153"
                                  }, 0.1)`,
                                  border: `1px solid rgba(${
                                    getTypeColor(project.project_type) ===
                                    "orange"
                                      ? "249, 115, 22"
                                      : getTypeColor(project.project_type) ===
                                        "violet"
                                      ? "168, 85, 247"
                                      : getTypeColor(project.project_type) ===
                                        "green"
                                      ? "34, 197, 94"
                                      : getTypeColor(project.project_type) ===
                                        "blue"
                                      ? "59, 130, 246"
                                      : "236, 72, 153"
                                  }, 0.2)`,
                                }}
                              >
                                {project.project_type}
                              </Badge>
                            </Table.Td>
                            <Table.Td style={{ border: "none" }}>
                              <Text
                                size="sm"
                                style={{ color: "rgba(161, 161, 170, 0.7)" }}
                              >
                                {new Date(
                                  project.created_at
                                ).toLocaleDateString()}
                              </Text>
                            </Table.Td>
                            <Table.Td style={{ border: "none" }}>
                              <Group gap="xs">
                                {project.project_url && (
                                  <ActionIcon
                                    size="sm"
                                    variant="light"
                                    color="blue"
                                    component="a"
                                    href={project.project_url}
                                    target="_blank"
                                    title="View Project"
                                    style={{
                                      background: "rgba(59, 130, 246, 0.1)",
                                      border:
                                        "1px solid rgba(59, 130, 246, 0.2)",
                                      color: "rgba(59, 130, 246, 0.9)",
                                    }}
                                  >
                                    <IconExternalLink size={14} />
                                  </ActionIcon>
                                )}
                                <ActionIcon
                                  size="sm"
                                  variant="light"
                                  color="orange"
                                  component={Link}
                                  href={`/admin/projects/${project.id}/edit`}
                                  title="Edit Project"
                                  style={{
                                    background: "rgba(249, 115, 22, 0.1)",
                                    border: "1px solid rgba(249, 115, 22, 0.2)",
                                    color: "rgba(249, 115, 22, 0.9)",
                                  }}
                                >
                                  <IconEdit size={14} />
                                </ActionIcon>
                                <ActionIcon
                                  size="sm"
                                  variant="light"
                                  color="red"
                                  onClick={() => handleDelete(project.id)}
                                  title="Delete Project"
                                  style={{
                                    background: "rgba(239, 68, 68, 0.1)",
                                    border: "1px solid rgba(239, 68, 68, 0.2)",
                                    color: "rgba(239, 68, 68, 0.9)",
                                  }}
                                >
                                  <IconTrash size={14} />
                                </ActionIcon>
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Paper>
                ) : (
                  // Mobile Card View
                  <Stack gap="md">
                    {projects.map((project) => (
                      <ProjectCard
                        key={project.id}
                        project={project}
                        onDelete={handleDelete}
                      />
                    ))}
                  </Stack>
                )}
              </>
            ) : (
              <Paper
                shadow="xl"
                radius="md"
                style={{
                  background: "rgba(26, 27, 30, 0.95)",
                  border: "1px solid rgba(55, 58, 64, 0.4)",
                }}
              >
                <Stack gap="md" align="center" py={60}>
                  <Text size="xl" style={{ color: "rgba(161, 161, 170, 0.7)" }}>
                    No projects yet
                  </Text>
                  <Text style={{ color: "rgba(161, 161, 170, 0.5)" }}>
                    Create your first project to get started
                  </Text>
                  <Button
                    variant="gradient"
                    gradient={{ from: "orange.6", to: "orange.7", deg: 135 }}
                    leftSection={<IconPlus size={16} />}
                    component={Link}
                    href="/admin/projects/new"
                    style={{ fontWeight: 600 }}
                  >
                    Create Project
                  </Button>
                </Stack>
              </Paper>
            )}
          </Stack>
        </Container>
      </Box>
    </AppShell>
  );
};

export default AdminProjectsIndex;
